import React, {
	Fragment,
	type ReactElement,
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { Label } from '@atlaskit/form';
import DeleteIcon from '@atlaskit/icon/core/delete';
import UploadIcon from '@atlaskit/icon/core/upload';
import Image from '@atlaskit/image';
import type { AuthProvider } from '@atlaskit/media-core';
import { Browser } from '@atlaskit/media-picker';
import type { Placement } from '@atlaskit/popper';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { EditorExperimentsConfig } from '@atlaskit/tmp-editor-statsig/experiments-config';
import { RovoHeaderImageGenerator, type GenerateImageProps } from '@atlassian/ai-image-generation';

import type { CustomMediaConfig } from '../../../../../controllers/use-product-dependencies';
import { defaultUploadingImageBackground } from '../../../../assets';
import { FormFieldErrorMessage } from '../../../../config-panel/form-field-error-messages';
import { PanelSubheader } from '../../../../config-panel/panel-subheader';
import { CustomImageUploadRenderer } from '../../../custom-image-upload-renderer';
import { BrowseButtonAndLinkInput } from '../../../unsplash/browse-button-and-link-input';
import { DefaultImagePicker } from '../../../unsplash/default-image-picker';
import { UnsplashSearchBar } from '../../../unsplash/search-bar';
import type { SelectedImage, UnsplashResult, UploadedImage } from '../../../unsplash/types';
import { isUnsplashImage, useUnsplash } from '../../../unsplash/utils';
import { useUploadMediaConfig } from '../../../use-upload-media-config';
import { getImageTypeToShow } from '../../../utils';
import { MediaContentHelperMessage } from '../helper-message';

import { messages } from './messages';

const CC_PAGE_EXPERIENCES_PREMIUM_MACROS_IMG_GENERATION =
	'cc_page_experiences_premium_macros_img_generation' as keyof EditorExperimentsConfig;

const bottomSpacing = xcss({ marginBottom: 'space.100' });
const removeBottomSpacing = xcss({ marginBottom: 'space.0' });

const uploadFileContainer = xcss({
	position: 'relative',
	borderRadius: 'radius.large',
	height: '124px',
	width: '100%',
	marginBottom: 'space.100',
	overflow: 'hidden',
});

const uploadFileButton = xcss({
	position: 'absolute',
	left: '30%',
	right: '30%',
	bottom: '30%',
	top: '30%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export type MediaPickerContentProps = {
	analyticsSource: string;
	confluencePageId?: string;
	contentId: string;
	customMediaConfig?: CustomMediaConfig;
	dropdownPopupComponentRef?: RefObject<HTMLDivElement | null>;
	elementIdPrefix: string;
	enableBottomSpacing?: boolean;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
	handleImageSelection: (props: SelectedImage | UploadedImage) => void;
	helperMessage?: string | ReactElement;
	imageAltText?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	isAiHeaderImageLoading?: boolean;
	isDisabled?: boolean;
	isNoImageAllowed?: boolean;
	isPageTitleComponent?: boolean;
	itemId?: string;
	placement?: Placement;
	sectionLabel: string | ReactElement;
	selectedImageUrl?: string;
	shouldAllowLinkFromUrl?: boolean;
	shouldShowDeleteButton?: boolean;
};

export const MediaPickerContent = ({
	itemId,
	elementIdPrefix,
	sectionLabel,
	imageAltText,
	selectedImageUrl,
	initialUnsplashResults,
	handleImageSelection,
	contentId,
	confluencePageId,
	analyticsSource,
	isPageTitleComponent = false,
	isNoImageAllowed = false,
	enableBottomSpacing = true,
	placement,
	dropdownPopupComponentRef,
	isDisabled = false,
	isAiHeaderImageLoading,
	helperMessage,
	shouldShowDeleteButton = false,
	customMediaConfig,
	shouldAllowLinkFromUrl = false,
	generateAIHeaderImage,
}: MediaPickerContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		selectedDefaultImageUrl,
		setDefaultUnsplashResults,
		defaultUnsplashResults,
		unSplashSearchResults,
		setUnsplashSearchResults,
		setError,
		handleError,
		errorMessage,
	} = useUnsplash(isPageTitleComponent, selectedImageUrl, initialUnsplashResults);

	const [newUploadedImage, setNewUploadedImage] = useState<UploadedImage | undefined>();

	const handleUploadedImage = useCallback(
		(props: UploadedImage) => {
			setNewUploadedImage(() => props);
			handleImageSelection(props);
		},
		[handleImageSelection],
	);

	const handleAIGenerate = useCallback(
		async (props: GenerateImageProps) => {
			if (!generateAIHeaderImage) {
				return undefined;
			}
			const mediaFileId = await generateAIHeaderImage(props);
			if (mediaFileId) {
				// Mirror the manual upload path so the picker's local state
				// (newUploadedImage) reflects each new AI-generated image,
				// preventing stale renders on re-generation.
				handleUploadedImage({
					uploadedImageId: mediaFileId,
					altDescription: '',
				} as UploadedImage);
			}
			return mediaFileId;
		},
		[generateAIHeaderImage, handleUploadedImage],
	);

	const handleDelete = () => {
		setNewUploadedImage(undefined);
		handleImageSelection({ fullSizeImageUrl: '', altDescription: '' });
	};

	const { showCustomUploadedImage } = getImageTypeToShow({
		imageSrc: newUploadedImage
			? newUploadedImage?.uploadedImageId
			: selectedDefaultImageUrl || undefined,
	});

	const selectedDefaultUploadedImage = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!selectedDefaultUploadedImage.current && !newUploadedImage && showCustomUploadedImage) {
			selectedDefaultUploadedImage.current = selectedDefaultImageUrl;
		}
	}, [newUploadedImage, showCustomUploadedImage, selectedDefaultImageUrl]);

	const customMediaAuthProvider = useMemo<AuthProvider | undefined>(() => {
		if (!customMediaConfig) {
			return undefined;
		}
		return () =>
			Promise.resolve({
				token: customMediaConfig.mediaToken,
				baseUrl: customMediaConfig.mediaUrl,
				asapIssuer: customMediaConfig.asapIssuer,
			});
	}, [customMediaConfig]);

	const {
		mediaConfig,
		handleUploadEnd,
		handleUploadStart,
		handleOnError,
		isMediaBrowserOpen,
		openMediaBrowser,
		closeMediaBrowser,
		isUploadingImage,
	} = useUploadMediaConfig({
		contentId,
		handleUploadedImageSelection: handleUploadedImage,
		analyticsSource,
	});

	const resolvedCollectionName = useMemo(() => {
		if (customMediaConfig) {
			return customMediaConfig.mediaCollectionName;
		}
		return mediaConfig?.collection;
	}, [customMediaConfig, mediaConfig]);

	const resolvedAuthProvider = useMemo<AuthProvider | undefined>(() => {
		return customMediaConfig ? customMediaAuthProvider : mediaConfig?.authProvider;
	}, [customMediaConfig, customMediaAuthProvider, mediaConfig]);

	const canOpenBrowser = Boolean(
		resolvedCollectionName && resolvedAuthProvider && openMediaBrowser,
	);

	const handleUploadButtonClick = useCallback(() => {
		if (!canOpenBrowser) {
			return;
		}

		openMediaBrowser!();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'customerImageUploadButton',
				source: analyticsSource,
			},
		}).fire();
	}, [canOpenBrowser, openMediaBrowser, createAnalyticsEvent, analyticsSource]);

	const unsplashSearchFieldId = itemId
		? `${elementIdPrefix}-search-unsplash-${itemId}`
		: `${elementIdPrefix}-search-unsplash`;

	const initialUnsplashFive = useMemo(
		() => defaultUnsplashResults?.slice(0, 5),
		[defaultUnsplashResults],
	);

	return (
		<>
			<Inline spread="space-between">
				<Label htmlFor={unsplashSearchFieldId}>
					<PanelSubheader>{sectionLabel}</PanelSubheader>
				</Label>
				{shouldShowDeleteButton && (
					<Box paddingBlockEnd="space.200">
						<IconButton
							isTooltipDisabled={false}
							label={formatMessage(messages.deleteImageButtonText)}
							icon={DeleteIcon}
							onClick={handleDelete}
							isDisabled={isDisabled}
							testId="remove-image-button"
						/>
					</Box>
				)}
			</Inline>

			<MediaContentHelperMessage isDisabled={isDisabled} helperMessage={helperMessage} />
			<Box xcss={uploadFileContainer}>
				{showCustomUploadedImage && !isUnsplashImage(selectedImageUrl) ? (
					<Box testId="custom-image-upload">
						<CustomImageUploadRenderer
							contentId={contentId}
							imageAltText={imageAltText}
							imageSrc={newUploadedImage?.uploadedImageId || selectedDefaultUploadedImage.current}
							mediaConfigOverride={{
								authProvider: customMediaAuthProvider,
								collection: customMediaConfig?.mediaCollectionName,
							}}
						/>
					</Box>
				) : (
					<Image
						height="124px"
						width="100%"
						src={defaultUploadingImageBackground}
						alt={formatMessage(messages.defaultBackgroundImageAltText)}
					/>
				)}
				<Box xcss={uploadFileButton}>
					<Button
						onClick={handleUploadButtonClick}
						isDisabled={isUploadingImage || isDisabled || !canOpenBrowser}
						testId="open-media-browser"
						appearance="primary"
						iconBefore={UploadIcon}
					>
						{formatMessage(messages.fileUploadButtonText)}
					</Button>
				</Box>
			</Box>
			{resolvedCollectionName && resolvedAuthProvider && (
				<Browser
					mediaClientConfig={{
						authProvider: resolvedAuthProvider,
						useSha256ForUploads: true,
					}}
					config={{
						multiple: false,
						fileExtensions: ['.jpg', '.jpeg', '.png', '.gif'],
						uploadParams: {
							collection: resolvedCollectionName,
							onUploadRejection: mediaConfig?.onUploadRejection,
						},
					}}
					isOpen={isMediaBrowserOpen}
					onClose={closeMediaBrowser}
					onUploadsStart={handleUploadStart}
					onError={handleOnError}
					onEnd={handleUploadEnd}
				/>
			)}
			{shouldAllowLinkFromUrl ? (
				<BrowseButtonAndLinkInput
					analyticsSource={analyticsSource}
					currentUrl={selectedDefaultImageUrl}
					handleImageSelection={handleImageSelection}
					isDisabled={isDisabled}
				/>
			) : (
				<Fragment>
					<UnsplashSearchBar
						elementIdPrefix={elementIdPrefix}
						unsplashSearchFieldId={unsplashSearchFieldId}
						handleImageSelection={handleImageSelection}
						analyticsSource={analyticsSource}
						setDefaultUnsplashResults={setDefaultUnsplashResults}
						defaultUnsplashResults={defaultUnsplashResults}
						unSplashSearchResults={unSplashSearchResults}
						setUnsplashSearchResults={setUnsplashSearchResults}
						setError={setError}
						handleError={handleError}
						placement={placement}
						dropdownPopupComponentRef={dropdownPopupComponentRef}
						isUploadingImage={isUploadingImage || isDisabled}
					/>
					<Box xcss={enableBottomSpacing ? bottomSpacing : removeBottomSpacing}>
						<DefaultImagePicker
							contentId={contentId}
							confluencePageId={confluencePageId}
							initialUnsplashResults={initialUnsplashFive}
							handleImageSelection={handleImageSelection}
							imageAltText={imageAltText}
							previewUploadedImageId={newUploadedImage?.uploadedImageId}
							initialUploadedImageId={selectedDefaultUploadedImage.current}
							selectedImageUrl={selectedDefaultImageUrl}
							analyticsSource={analyticsSource}
							isUploadingImage={isUploadingImage || isDisabled}
							isNoImageAllowed={isNoImageAllowed && !isPageTitleComponent}
							mediaConfigOverride={{
								authProvider: customMediaAuthProvider,
								collection: customMediaConfig?.mediaCollectionName,
							}}
						/>
						{errorMessage && <FormFieldErrorMessage>{errorMessage}</FormFieldErrorMessage>}
					</Box>
				</Fragment>
			)}
			{expValEquals(CC_PAGE_EXPERIENCES_PREMIUM_MACROS_IMG_GENERATION, 'isEnabled', true) && (
				<RovoHeaderImageGenerator
					generateAIHeaderImage={generateAIHeaderImage ? handleAIGenerate : undefined}
					isAiHeaderImageLoading={isAiHeaderImageLoading}
				/>
			)}
		</>
	);
};
